/* eslint-disable no-underscore-dangle */
import { LitElement, html } from 'lit-element';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { createPopper } from '@popperjs/core';
import format from 'date-fns/esm/format';
import parse from 'date-fns/esm/parse';
import addDays from 'date-fns/esm/addDays';
import addMonths from 'date-fns/esm/addMonths';
import addYears from 'date-fns/esm/addYears';
import enUS from 'date-fns/esm/locale/en-US';
import nl from 'date-fns/esm/locale/nl';
import fr from 'date-fns/esm/locale/fr';
import de from 'date-fns/esm/locale/de';
import endOfMonth from 'date-fns/esm/endOfMonth';
import getDay from 'date-fns/esm/getDay';
import getMonth from 'date-fns/esm/getMonth';
import getYear from 'date-fns/esm/getYear';
import subMonths from 'date-fns/esm/subMonths';
import subYears from 'date-fns/esm/subYears';
import startOfDay from 'date-fns/esm/startOfDay';

import { n100 } from '@aab/sc-styles-colors';
import AabInput from '@aab/sc-aab-input/src/AabInput';
import AabInputMessage from '@aab/sc-aab-inputmessage/src/AabInputmessage';
import AabIcon from '@aab/sc-aab-icon/src/AabIcon';
import {
  sy_arrow_arrow_left as syArrowArrowLeft,
  sy_arrow_arrow_right as syArrowArrowRight,
  sy_datetime_calender as syDatetimeCalender,
} from '@aab/sc-aab-icon-set/src/AabIconSet';
import DatepickerCell from './Datepicker-cell';

import aabDatepickerStyles from './styles/aab-datepicker-style';
import styles from './styles/styles';

const locales = { en: enUS, nl, fr, de };
const CONSTANTS = {
  firstYearPossible: 1908,
  lastYearPossible: 2099,
  lastDayOfWeek: 6
};

export default class AabDatepicker extends ScopedElementsMixin(LitElement) {
  constructor() {
    super();
    this.dateFormat = 'dd-MM-yyyy';
    this.locale = 'en';
    this.dayNamesOfTheWeek = [];
    this.monthsList = [];
    this.yearsList = [];
    this.yearsGroupToShow = [];
    this.currentDate = parseInt(format(startOfDay(Date.now()), 't'), 10);
    this.showDaysView = true;
    this.yearsGroupIndex = 9;
    this.passedStartDate = null;
    this.passedEndDate = null;
    this.placeHolder = 'dd-mm-yyyy';
  }

  static get properties() {
    return {
      // parameter values that is set by user
      withRange: { type: Boolean, attribute: 'with-range' },
      locale: { type: String },
      leftLabel: { type: String, attribute: 'left-label' },
      leftMessage: { type: String, attribute: 'left-message' },
      leftMessageType: { type: String, attribute: 'left-message-type' },
      rightLabel: { type: String, attribute: 'right-label' },
      rightMessage: { type: String, attribute: 'right-message'},
      rightMessageType: { type: String, attribute: 'right-message-type' },
      disabled: { type: Boolean },
      fromInputInvalid: { type: Boolean, attribute: 'from-input-invalid' },
      toInputInvalid: { type: Boolean, attribute: 'to-input-invalid' },
      minSelectableDate: { type: Number, attribute: 'min-selectable-date' },
      maxSelectableDate: { type: Number, attribute: 'max-selectable-date' },
      passedStartDate: { type: Number, attribute: 'start-date' },
      passedEndDate: { type: Number, attribute: 'end-date' },
      // local variables
      dateFrom: { type: Number, attribute: false },
      dateTo: { type: Number, attribute: false },
      month: { type: String, attribute: false },
      year: { type: Number, attribute: false },
      hoveredDate: { type: String, attribute: false },
      monthPlus: { type: String, attribute: false },
      yearPlus: { type: Number, attribute: false },
      placeHolder: { type: String, attribute: false }
    };
  }

  static get styles() {
    return [styles, aabDatepickerStyles];
  }

  static get scopedElements() {
    return {
      'aab-icon': AabIcon,
      'datepicker-cell': DatepickerCell,
      'aab-input': AabInput,
      'aab-inputmessage': AabInputMessage,
    };
  }

  firstUpdated() {
    this.firstUnixPossible = parseInt(format(startOfDay(new Date(`01/01/${CONSTANTS.firstYearPossible}`)), 't'), 10);
    this.lastUnixPossible = parseInt(format(startOfDay(new Date(`12/31/${CONSTANTS.lastYearPossible}`)), 't'), 10);
    const calenderElement = this.shadowRoot.querySelector('#aab-calender');
    window.addEventListener('click', () => this.hideCalendar(calenderElement));
    this.monthsList = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
    this.setYears(CONSTANTS.firstYearPossible, CONSTANTS.lastYearPossible, 12);

    if (this.passedStartDate !== null && this.isInRange(this.minSelectableDate, this.maxSelectableDate, this.passedStartDate)) {
      this.dateFromChanged(this.passedStartDate);
    }
    if (this.passedEndDate !== null && this.passedEndDate > this.passedStartDate && this.isInRange(this.minSelectableDate, this.maxSelectableDate, this.passedEndDate)) {
      this.dateToChanged(this.passedEndDate);
    }
  }

  updated(properties) {
    if (properties.has('withRange') && !this.withRange && properties.get('withRange')) {
      this.dateTo = undefined;
      this.hoveredDate = undefined;
    }

    if (properties.has('locale')) {
      this.localeChanged();
      this.yearAndMonthChanged(this.year, this.month);
    }

    if (properties.has('dateTo') && this.dateTo !== undefined) {
      this.hideCalendar(this.shadowRoot.querySelector('#aab-calender'));
    }

    if (properties.has('dateFrom')) {
      if (!this.withRange && this.dateFrom !== undefined) {
        this.hideCalendar(this.shadowRoot.querySelector('#aab-calender'));
      }
      if (this.dateFrom !== undefined) {
        const { month, year } = this.getMonthAndYearFromDate(new Date(this.dateFrom * 1000));
        this.month = month;
        this.year = year;
      }
    }

    if (properties.has('year') || properties.has('month')) {
      this.yearAndMonthChanged(this.year, this.month);
      this.monthChanged(this.month, this.year);
    }

    if (properties.has('minSelectableDate')) {
      /** below line to insure to use first possible 'unix time' in the given day is used inside the component
       * in case 'middle of the day value is passed'
       */
      this.minSelectableDateStartAt = this.formatToUnix(this.formatDate(this.minSelectableDate));
    }

    if (properties.has('maxSelectableDate')) {
      /** below line to insure to use first possible 'unix time' in the given day is used inside the component
       * in case 'middle of the day value is passed'
       */
      this.maxSelectableDateStartAt = this.formatToUnix(this.formatDate(this.maxSelectableDate));
    }
  }

  // here we remove listener to stop memory leak
  disconnectedCallback() {
    const calenderElement = this.shadowRoot.querySelector('#aab-calender');
    window.removeEventListener('click', () => this.hideCalendar(calenderElement));
    super.disconnectedCallback();
  }

  hideCalendar(el) {
    if (el.classList.contains('calendar-show')) {
      el.classList.remove('calendar-show', 'd-block', 'position-absolute');
      el.classList.add('d-none');
    }
  }

  // we used the function below because IE doesn't support Number.isNaN
  checkIsNaN(o) {
    return o !== o;
  }

  isInRange(min, max, value) {
    if ((min <= value && value <= max) || (min === undefined && max === undefined)) {
      return true;
    }
    if (min === undefined && max !== undefined) {
      return value <= max;
    }
    if (min !== undefined && max === undefined) {
      return value >= min;
    }
    return false;
  }

  // this function take a string of date and return it formatted in Unix date stamp, if it is a valid date
  formatToUnix(StringDate) {
    const date = parse(StringDate, this.dateFormat, new Date());
    return this.checkIsNaN(date.getTime()) ? undefined : parseInt(format(date, 't'), 10);
  }

  getMonthAndYearFromDate(date) {
    let month = date.getMonth() + 1;
    month = (month < 10) ? `0${month}` : month.toString();
    const year = date.getFullYear();
    return { month, year };
  }

  handleOpenOrCloseCalender(event) {
    if (!this.disabled) {
      const calenderElement = this.shadowRoot.querySelector('#aab-calender');
      if (calenderElement.classList.contains('calendar-show')) {
        this.hideCalendar(calenderElement);
      } else {
        calenderElement.classList.add('calendar-show', 'd-block', 'position-absolute');
        calenderElement.classList.remove('d-none');
        this.handleShowDaysView();
        createPopper(this.shadowRoot.querySelector('#main-container'), calenderElement, {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 1],
              },
            },
          ]
        });
      }
    }
    event.stopPropagation();
  }

  // this function take unix date stamp and return it as formatted string
  formatDate(date) {
    this.dateFormat = this.locale === 'de' ? 'dd.MM.yyyy' : 'dd-MM-yyyy';
    return date === 0 || date ? format(parse(date, 't', new Date()), this.dateFormat, { locale: locales[this.locale], awareOfUnicodeTokens: true }) : '';
  }

  // this function takes the inputted value and autopopulates it with dashes or dots
  autoPopulateDate(date) {
    let autoPopulatedDate = date;

    // checks for 01022003 (date without formatting)
    if (date.length === 8 && (date.match(/^[0-9]+$/) !== null)) {
      const regexPattern = this.locale === 'de' ? '$1.$2.' : '$1-$2-';
      autoPopulatedDate = date.replace(/^(.{2})(.{2})/, regexPattern);
      // checks for 01/02/2003 (date with slash formatting)
    } else if (date.length === 10 && (date.match(/^(0?[1-9]|[12][0-9]|3[01])[/](0?[1-9]|1[012])[/]\d{4}$/) !== null)) {
      const regexPattern = this.locale === 'de' ? '.' : '-';
      autoPopulatedDate = date.replace(/\//g, regexPattern);
    }
    return autoPopulatedDate;
  }

  // this function validate the user input in DateFrom field and act accordingly
  dateFromChangedFromInput(event) {
    /** the check below is needed for IE only since it is trigger focusout event
     *  for the aab-icon inside the aab-input which should be ignored.
     */
    if (event.target.getAttribute('data-tag-name') === 'aab-input') {
      const date = this.autoPopulateDate(event.target.value);
      const dateUnix = this.formatToUnix(date);
      if (dateUnix || dateUnix === 0) {
        if ((this.maxSelectableDateStartAt && dateUnix > this.maxSelectableDateStartAt) || (this.minSelectableDateStartAt && dateUnix < this.minSelectableDateStartAt)
              || dateUnix > this.lastUnixPossible || dateUnix < this.firstUnixPossible) {
          this.dateFrom = undefined;
          this.emitEvent(dateUnix, 'date-from-out-of-range');
        } else {
          this.dateFromChanged(dateUnix);
          if (this.dateTo !== undefined && this.dateTo < dateUnix) {
            this.emitEvent(this.dateTo, 'date-to-out-of-range');
            this.dateTo = undefined;
          }
        }
      } else {
        this.dateFrom = undefined;
        this.emitEvent(dateUnix, 'invalid-date-from');
      }
    }
  }

  // this function validate the user input in dateTo field and act accordingly
  dateToChangedFromInput(event) {
    /** the check below is needed for IE only since it is trigger focusout event
     *  for the aab-icon inside the aab-input which should be ignored.
     */
    if (event.target.getAttribute('data-tag-name') === 'aab-input') {
      const date = this.autoPopulateDate(event.target.value);
      const dateUnix = this.formatToUnix(date);
      if (dateUnix || dateUnix === 0) {
        if ((this.dateFrom === undefined) || (dateUnix < this.dateFrom)
          || (this.maxSelectableDateStartAt && dateUnix > this.maxSelectableDateStartAt) || (this.minSelectableDateStartAt && dateUnix < this.minSelectableDateStartAt)
          || dateUnix > this.lastUnixPossible || dateUnix < this.firstUnixPossible) {
          this.dateTo = undefined;
          this.emitEvent(dateUnix, 'date-to-out-of-range');
        } else {
          this.dateToChanged(dateUnix);
        }
      } else {
        this.dateTo = undefined;
        this.emitEvent(dateUnix, 'invalid-date-to');
      }
    }
  }

  /** this function will set new value to dateTo and call emitEvent
   * when called from dateToChangedFromInput (input field) or from handleDateSelected (input from calender).
   */
  dateToChanged(dateUnix) {
    this.dateTo = dateUnix;
    this.emitEvent(dateUnix, 'date-to-changed');
  }

  /** this function will set new value of dateFrom and call emitEvent
   * when called from dateFromChangedFromInput (input field) or from handleDateSelected (input from calender).
   */
  dateFromChanged(dateUnix) {
    this.dateFrom = dateUnix;
    this.emitEvent(dateUnix, 'date-from-changed');
  }

  emitEvent(passedValue, eventType) {
    this.dispatchEvent(
      new CustomEvent(
        `aab-datepicker-${eventType}`,
        {
          detail: {
            value: passedValue,
          },
          bubbles: true,
          composed: true,
        }
      )
    );
  }

  isCurrentDate(dayOfMonth) {
    return dayOfMonth.date === this.currentDate;
  }

  isPastDate(dayOfMonth) {
    return dayOfMonth.date < this.currentDate;
  }

  // this function will create this new days view when "year" or "month" are changed.
  yearAndMonthChanged(year, month) {
    if (year && month) {
      const monthMinus = month.substring(month.length - 2);
      const getStartDateString = {
        de: `01.${monthMinus}.${year}`,
      };
      let startDateString = getStartDateString[this.locale] || `01-${monthMinus}-${year}`;
      let startDateFn = parse(startDateString, this.dateFormat, new Date(), { awareOfUnicodeTokens: true });
      const endDateFn = endOfMonth(startDateFn);
      const endDateString = format(endDateFn, this.dateFormat, { awareOfUnicodeTokens: true });
      const firstDayOfWeek = locales[this.locale].options.weekStartsOn;
      const rows = [];
      let columns = [];

      while (startDateString !== endDateString) {
        let dayNumberFn = getDay(startDateFn) - firstDayOfWeek;
        if (dayNumberFn < 0) {
          dayNumberFn = CONSTANTS.lastDayOfWeek;
        }

        const columnFn = {
          hover: false,
          date: parseInt(format(startDateFn, 't'), 10),
          title: parseInt(format(startDateFn, 'd', { awareOfUnicodeTokens: true }), 10),
        };
        columns.push(columnFn);

        if (dayNumberFn === CONSTANTS.lastDayOfWeek) {
          for (let i = columns.length; i < CONSTANTS.lastDayOfWeek + 1; i += 1) {
            columns.unshift(0);
          }
          rows.push(columns.slice());
          columns = [];
        }

        startDateFn = addDays(startDateFn, 1);
        startDateString = format(startDateFn, this.dateFormat, { awareOfUnicodeTokens: true });

        if (startDateString === endDateString) {
          const endColumnFn = {
            hover: false,
            date: parseInt(format(startDateFn, 't'), 10),
            title: parseInt(format(startDateFn, 'd', { awareOfUnicodeTokens: true }), 10),
          };
          columns.push(endColumnFn);
          for (let i = columns.length; i <= CONSTANTS.lastDayOfWeek; i += 1) {
            columns.push(0);
          }
          rows.push(columns.slice());
          columns = [];
        }
      }
      this.daysOfMonth = rows;
    }
  }

  computeCurrentMonthName(month, year) {
    return month && year ? format(parse(`${month}-${year}`, 'MM-yyyy', new Date()), 'MMMM', { locale: locales[this.locale] }) : '';
  }

  tdIsEnabled(day) {
    return day ? 'enabled' : '';
  }

  // this function will handle the date selected from calendar.
  handleDateSelected({ detail }) {
    const { date } = detail;
    if (this.withRange) {
      if (this.dateFrom !== undefined && this.dateTo !== undefined) {
        this.dateFromChanged(date);
        this.dateTo = undefined;
        this.hoveredDate = undefined;
      } else if (this.dateFrom === undefined || (this.dateFrom !== undefined && date < this.dateFrom)) {
        this.dateFromChanged(date);
      } else {
        this.dateToChanged(date);
      }
    } else {
      this.dateFromChanged(date);
    }
  }

  handleDateHovered(event) {
    if (this.withRange) {
      this.hoveredDate = event.detail.date;
    }
  }

  // this function will be called when next month arrow is clicked
  handleNextMonth() {
    const month = parse(this.month, 'MM', new Date());
    const monthPlusDate = addMonths(month, 1);
    const monthPlusString = format(monthPlusDate, 'MM', { locale: locales[this.locale] });

    this.month = monthPlusString;
    if (this.month === '01') {
      const year = parse(this.year, 'yyyy', new Date());
      const yearPlusDate = addYears(year, 1);
      const yearPlusString = format(yearPlusDate, 'yyyy', { locale: locales[this.locale] });
      this.year = parseInt(yearPlusString, 10);
    }
  }

  // this function will be called when previous month arrow is clicked
  handlePrevMonth() {
    const month = parse(this.month, 'MM', new Date());
    const monthMinusDate = subMonths(month, 1);
    const monthMinusString = format(monthMinusDate, 'MM', { locale: locales[this.locale] });

    this.month = monthMinusString;
    if (this.month === '12') {
      const year = parse(this.year, 'yyyy', new Date());
      const yearMinusDate = subYears(year, 1);
      const yearMinusString = format(yearMinusDate, 'yyyy', { locale: locales[this.locale] });
      this.year = parseInt(yearMinusString, 10);
    }
  }

  // this function will be called when previous year arrow is clicked
  handlePrevYear() {
    if (this.year > CONSTANTS.firstYearPossible){
      this.year = this.year - 1;
    }
  }

  // this function will be called when next year arrow is clicked
  handleNextYear() {
    if (this.year < CONSTANTS.lastYearPossible){
      this.year = this.year + 1;
    }
  }

  // this function will be called when previous year group arrow is clicked
  handlePrevYearsGroup() {
    if (this.yearsGroupIndex > 0) {
      this.yearsGroupIndex = this.yearsGroupIndex - 1;
      this.yearsGroupToShow = this.yearsList[this.yearsGroupIndex];
      this.requestUpdate();
    }
  }

  // this function will be called when next year group arrow is clicked
  handleNextYearsGroup() {
    if (this.yearsGroupIndex < 15) {
      this.yearsGroupIndex = this.yearsGroupIndex + 1;
      this.yearsGroupToShow = this.yearsList[this.yearsGroupIndex];
      this.requestUpdate();
    }
  }

  // this function will create the years list in two dimensional array to move between of them set group at a time
  setYears(from, to, groupOf) {
    const yearsList = [];
    for (let a = from; a <= to; a += 1) {
      yearsList.push(a);
    };
    const groupedYearsList = yearsList.reduce((rows, key, index) => (index % groupOf === 0 ? rows.push([key])
      : rows[rows.length-1].push(key)) && rows, []);
    this.yearsList = groupedYearsList;
    this.yearsGroupToShow = this.yearsList[this.yearsGroupIndex];
  }

  handleShowMonthsView() {
    this.showMonthsView = true;
    this.showDaysView = false;
    this.showYearsView = false;
    this.requestUpdate();
  }

  handleShowDaysView() {
    this.showMonthsView = false;
    this.showDaysView = true;
    this.showYearsView = false;
    this.requestUpdate();
  }

  handleShowYearsView() {
    this.showMonthsView = false;
    this.showDaysView = false;
    this.showYearsView = true;
    this.requestUpdate();
  }

  // this function will render the view if NL / EN / FR / DE locale change and also on initiating the component
  localeChanged() {
    if (!this.month) {
      this.month = this.dateFrom ? `0${getMonth(parse(this.dateFrom, 't', new Date())) + 1}`.slice(-2)
        : format(new Date(), 'MM', { awareOfUnicodeTokens: true });
    }
    if (!this.year) {
      this.year = this.dateFrom ? getYear(parse(this.dateFrom, 't', new Date())) : parseInt(format(new Date(), 'yyyy'), 10);
    }
    if (locales[this.locale]) {
      const dayNamesOfTheWeek = [];
      let i = 0;
      for (i; i < 7; i += 1) {
        dayNamesOfTheWeek.push(locales[this.locale].localize.day(i, { width: 'short' }));
      }

      const firstDayOfWeek = locales[this.locale].options.weekStartsOn;
      const tmp = dayNamesOfTheWeek.slice().splice(0, firstDayOfWeek);
      this.dayNamesOfTheWeek = dayNamesOfTheWeek
        .slice()
        .splice(firstDayOfWeek, dayNamesOfTheWeek.length)
        .concat(tmp);
    }
    // for 'de' language the placeholder should be translated to 'TT.MM.JJJJ'. The format stays the same, eg: 24.04.2021
    this.placeHolder = this.locale === 'de' ? 'TT.MM.JJJJ' : 'dd-mm-yyyy';
  }

  // this function will increase or decrease the year when month change in case it is needed
  monthChanged(month, year) {
    if (year && month) {
      const monthPlus = `0${((month % 12) + 1)}`;
      this.monthPlus = monthPlus.substring(monthPlus.length - 2);
      if (this.monthPlus === '01') {
        this.yearPlus = parseInt(year, 10) + 1;
      } else {
        this.yearPlus = parseInt(year, 10);
      }
    }
  }

  daysViewRender() {
    return html`
    <div id="days-view">
      <div class="d-flex align-items-center text-center mt-2 monthName">
         <aab-icon svg="${syArrowArrowLeft}" size="3" class="sy_arrow_chevron_left" @click="${this.handlePrevMonth}" id="days-prev-arrow"
               role="button" aria-label="go to previous month" tabindex="0"></aab-icon>
        <div id="day-view-header" class="flex d-flex align-items-center justify-content-center"
             @click="${this.handleShowYearsView}" role="heading" aria-level="4" tabindex="0">
          <div class="mr-1" test-id="days-view-title">${this.computeCurrentMonthName(this.month, this.year)}</div>
          <div class="mr-1">${this.year}</div>
        </div>
        <aab-icon svg="${syArrowArrowRight}" size="3" class="sy_arrow_chevron_right" @click="${this.handleNextMonth}" id="days-next-arrow"
              role="button" aria-label="go to next month" tabindex="0"></aab-icon>
      </div>
      <div class="d-table mt-2 appear" role="table">
        <div class="tr">
          ${this.dayNamesOfTheWeek && this.dayNamesOfTheWeek.map((dayNameOfWeek) => html`
               <div class="th d-table-cell p-0 m-0 text-center">${dayNameOfWeek}</div>
            `)}
        </div>
        <div>
          ${this.daysOfMonth && this.daysOfMonth.map((week) => html`
          <div class="tr d-table-row" role="row">
            ${week && week.map((dayOfMonth) => html`
              <div class="td d-table-cell p-0 m-0 ${this.tdIsEnabled(dayOfMonth)}" role="gridcell"
                   aria-selected="${dayOfMonth && (dayOfMonth.date === this.dateFrom || dayOfMonth.date === this.dateTo) ? true : false}"
                   aria-disabled="${this.minSelectableDateStartAt > dayOfMonth.date || this.maxSelectableDateStartAt < dayOfMonth.date ? true : false}">
                ${dayOfMonth ? html`
                  <datepicker-cell
                    .minSelectableDate="${this.minSelectableDateStartAt}"
                    .maxSelectableDate="${this.maxSelectableDateStartAt}"
                    .month="${this.month}"
                    .hoveredDate="${this.hoveredDate}"
                    .dateTo="${this.dateTo}"
                    .dateFrom="${this.dateFrom}"
                    .day="${dayOfMonth}"
                    .isCurrentDate="${this.isCurrentDate(dayOfMonth)}"
                    .isPastDate="${this.isPastDate(dayOfMonth)}"
                    @date-is-hovered="${this.handleDateHovered}"
                    @date-is-selected="${this.handleDateSelected}"></datepicker-cell>
                ` : null}
              </div>`)}
          </div>`)}
        </div>
      </div>
    </div>`;
  }

  monthsViewRender() {
    return html`
      <div id="months-view">
         <div class="d-flex align-items-center text-center mt-2 monthName">
         <aab-icon svg="${syArrowArrowLeft}" size="3" class="sy_arrow_chevron_left" @click="${this.handlePrevYear}" id="months-prev-arrow"
               role="button"  aria-label="go to previous year" tabindex="0">
         </aab-icon>
          <div id="month-view-header" class="flex d-flex align-items-center justify-content-center"
               @click="${this.handleShowDaysView}" role="heading" aria-level="4" tabindex="0">
            <spin>${this.year}</spin>
          </div>
          <aab-icon svg="${syArrowArrowRight}" size="3" class="sy_arrow_chevron_right" @click="${this.handleNextYear}" id="months-next-arrow"
                role="button" aria-label="go to next year" tabindex="0">
          </aab-icon>
        </div>
        <div class="d-flex flex-wrap justify-content-center mt-2 appear" role="list">
          ${this.monthsList && this.monthsList.map((monthList, index) => html`
            <div class="month-block d-flex justify-content-center align-items-center
                  ${(`0${getMonth(new Date()) + 1}`.slice(-2)) === monthList ? 'currentMonth' : null}
                  ${this.dateFrom !== undefined && (`0${getMonth(parse(this.dateFrom, 't', new Date())) + 1}`.slice(-2)) === monthList ? 'selectedMonth' : null}"
                 @click="${() => { this.handleShowDaysView();
      this.month = ((index + 1).toString());}}"
                id="${`month-from-list-${index}`}" role="listitem" tabindex="0">${this.computeCurrentMonthName(monthList, this.year)}
             </div>`)}
        </div>
      </div>`;
  }

  yearsViewRender() {
    return html`
     <div id="years-view">
      <div class="d-flex align-items-center text-center mt-2 monthName">
        <aab-icon svg="${syArrowArrowLeft}" size="3" class="sy_arrow_chevron_left" @click="${this.handlePrevYearsGroup}" id="years-prev-arrow"
              role="button" aria-label="go to previous years group" tabindex="0">
        </aab-icon>
        <div id="year-view-header" class="flex d-flex align-items-center justify-content-center"
             @click="${this.handleShowDaysView}" role="heading" aria-level="4" tabindex="0">
          <spin>${this.yearsGroupToShow[0]} - ${this.yearsGroupToShow[this.yearsGroupToShow.length - 1]}</spin>
        </div>
        <aab-icon svg="${syArrowArrowRight}" size="3" class="sy_arrow_chevron_right" @click="${this.handleNextYearsGroup}" id="years-next-arrow"
              role="button" aria-label="go to next years group" tabindex="0">
        </aab-icon>
      </div>
      <div class="d-flex flex-wrap justify-content-center mt-2 appear" role="list">
        ${this.yearsGroupToShow && this.yearsGroupToShow.map((year, index) => html`<div class="month-block d-flex justify-content-center align-items-center
          ${getYear(new Date()) === year ? 'currentYear' : null}
          ${this.dateFrom !== undefined && getYear(parse(this.dateFrom, 't', new Date())) === year ? 'selectedYear' : null}"
            @click="${() => { this.handleShowMonthsView();
      this.year = year;}}"
            id="${`year-from-list-${index}`}" role="listitem" tabindex="0">${year}</div>`)}
      </div>
    </div>`;
  }

  render() {
    return html`
      <div id="main-container">
        <div class="d-flex align-items-end">
          <div class="${this.withRange ? 'smr-2' : ''} from-input-container">
            <aab-input id="from-input"
                .value=${this.formatDate(this.dateFrom)}
                label=${this.leftLabel}
                type="text"
                maxlength="10"
                placeholder=${this.placeHolder}
                ?required=${this.required}
                ?disabled=${this.disabled}
                ?invalid=${this.fromInputInvalid}
                @focusout=${this.dateFromChangedFromInput}>
              <aab-icon slot="icon" size="3" color=${this.disabled ? n100 : ''} svg=${syDatetimeCalender}
                  @click="${this.handleOpenOrCloseCalender}" class="sy_datetime_calender ${this.disabled ? 'disabled-icon' : ''}"
                  aria-controls="aab-calender" aria-label="Open or Close calender" tabindex="0" /></aab-icon>
            </aab-input>
          </div>
          ${this.withRange ? html`
            <div class="to-input-container">
              <aab-input id="to-input"
                  .value=${this.formatDate(this.dateTo)}
                  label=${this.rightLabel}
                  type="text"
                  maxlength="10"
                  placeholder=${this.placeHolder}
                  ?required=${this.required}
                  ?disabled=${this.disabled}
                  ?invalid=${this.toInputInvalid}
                  @focusout=${this.dateToChangedFromInput}>
                <aab-icon slot="icon" size="3" color=${this.disabled ? n100 : ''} svg=${syDatetimeCalender}
                    @click="${this.handleOpenOrCloseCalender}" class="sy_datetime_calender ${this.disabled ? 'disabled-icon' : ''}"
                    aria-controls="aab-calender" aria-label="Open or Close calender" tabindex="0" /></aab-icon>
              </aab-input>
            </div>
          ` : null}
        </div>
        <div class="d-flex">
            ${this.leftMessage || this.rightMessage ? html`
            <div class="from-input-inputmessage-container ${this.withRange ? 'smr-2' : ''} mt-1">
                <aab-inputmessage id="left-message" icon message=${this.leftMessage} type=${this.leftMessageType}></aab-inputmessage>
            </div>` : ''}
            ${this.rightMessage && this.withRange ? html`
            <div class="to-input-inputmessage-container mt-1">
                <aab-inputmessage id="right-message" icon message=${this.rightMessage} type=${this.rightMessageType}></aab-inputmessage>
            </div>` : ''}
        </div>
      </div>
      <div id="aab-calender" class="d-none" @click="${(event) => event.stopPropagation()}">
        <div class="shadow-card rounded-2 p-1 d-block" id="datepicker-calendar"
             role="region" aria-label="calender">
            <div>${this.showDaysView ? this.daysViewRender() : ''}</div>
            <div>${this.showMonthsView ? this.monthsViewRender() : ''}</div>
            <div>${this.showYearsView ? this.yearsViewRender() : ''}</div>
        </div>
      </div>`;
  }
}
